/**
 * 播放器工具函数
 * 参考旧项目 tv-linux 的播放器实现
 */

import type { QualityItem, SpeedItem, HistoryItem } from '@/types/player';
import { usePlayerStore, PLAYER_STORAGE_KEYS } from '@/stores/playerStore';
import { useHistoryStore } from '@/stores/history';
import { logger } from './devUtils';

/**
 * 倍速列表配置
 * 参考 tv-linux/src/views/newParticular/newParticular.js:171-178
 */
export const SPEED_LIST: SpeedItem[] = [
  { id: '0.5', name: '0.5X', rate: 0.5 },
  { id: '0.75', name: '0.75X', rate: 0.75 },
  { id: '1', name: '1X', rate: 1 },
  { id: '1.25', name: '1.25X', rate: 1.25 },
  { id: '1.5', name: '1.5X', rate: 1.5 },
  { id: '2', name: '2X', rate: 2 },
];

// 重新导出 PLAYER_STORAGE_KEYS 以保持向后兼容
export { PLAYER_STORAGE_KEYS };

/**
 * 格式化时间（秒 -> HH:MM:SS 或 MM:SS）
 * 参考 tv-linux/src/util/util.js timeFilter 函数
 */
export function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * 获取清晰度索引
 *
 * @param qualityList 清晰度列表
 * @param targetQuality 目标清晰度名称（内部格式如 'fhd', 'shd'）
 * @returns 清晰度索引
 */
export function getQualityIndex(qualityList: QualityItem[], targetQuality?: string): number {
  if (!qualityList || qualityList.length === 0) {
    return 0;
  }

  // 从 localStorage 获取用户上次选择的清晰度（内部格式）
  const savedQuality =
    targetQuality || (typeof window !== 'undefined' ? localStorage.getItem(PLAYER_STORAGE_KEYS.QUALITY) : null);

  if (savedQuality) {
    const index = qualityList.findIndex((item) => item.name === savedQuality);
    if (index !== -1) return index;
  }

  // 默认返回第二档（通常是高清）
  return Math.min(1, qualityList.length - 1);
}

/**
 * 获取倍速索引
 *
 * @param rate 倍速值
 * @returns 倍速索引
 */
export function getSpeedIndex(rate: number): number {
  const index = SPEED_LIST.findIndex((item) => item.rate === rate);
  return index !== -1 ? index : 2; // 默认1x
}

/**
 * 从LocalStorage获取历史记录
 *
 * @param cid 专辑ID
 * @param vid 视频ID
 * @returns 播放时间（秒），如果没有返回0
 */
export function getHistoryFromLocal(cid: string, vid: string): number {
  if (typeof window === 'undefined') return 0;

  try {
    const historyKey = `${cid}_${vid}`;
    const historyDataStr = localStorage.getItem(historyKey);

    if (historyDataStr) {
      const historyData: HistoryItem = JSON.parse(historyDataStr);
      return historyData.playTime || 0;
    }
  } catch (error) {
    logger.error('[player] 获取历史记录失败:', error);
  }

  return 0;
}

/**
 * 检查是否应该跳过片头
 * 参考 tv-linux/src/views/newParticular/newParticular.js:5288-5298
 *
 * @param currentTime 当前播放时间
 * @param headTimeBegin 片头开始时间
 * @param headTime 片头结束时间
 * @param isHeadSkipped 是否已跳过
 * @param skipIntroEnabled 跳过片头片尾设置（true: 跳过, false: 不跳过）
 * @returns 是否应该跳过
 */
export function shouldSkipIntro(
  currentTime: number,
  headTimeBegin: number,
  headTime: number,
  isHeadSkipped: boolean,
  skipIntroEnabled = true
): boolean {
  // 如果设置为不跳过，直接返回 false
  if (!skipIntroEnabled) return false;

  if (isHeadSkipped) return false;
  if (headTimeBegin === 0 && headTime === 0) return false;

  return currentTime >= headTimeBegin && currentTime < headTime;
}

/**
 * 检查是否应该跳过片尾
 * 参考 tv-linux/src/views/newParticular/newParticular.js:5301-5332
 *
 * @param currentTime 当前播放时间
 * @param totalTime 视频总时长
 * @param tailTime 片尾开始时间（从结束往前数）
 * @param isTailSkipped 是否已跳过
 * @param skipIntroEnabled 跳过片头片尾设置（true: 跳过, false: 不跳过）
 * @returns 是否应该跳过
 */
export function shouldSkipOutro(
  currentTime: number,
  totalTime: number,
  tailTime: number,
  isTailSkipped: boolean,
  skipIntroEnabled = true
): boolean {
  // 如果设置为不跳过，直接返回 false
  if (!skipIntroEnabled) return false;

  if (isTailSkipped) return false;
  if (totalTime === 0) return false;

  const actualTailTimeStart = totalTime - tailTime;

  if (actualTailTimeStart < 0) return false;

  return currentTime >= actualTailTimeStart;
}

/**
 * 获取跳过片头片尾配置
 *
 * @returns 是否启用跳过
 */
export function getSkipIntroConfig(): boolean {
  if (typeof window === 'undefined') return false;
  const skipIntro = localStorage.getItem(PLAYER_STORAGE_KEYS.SKIP_INTRO);
  return skipIntro === '1';
}

/**
 * 设置跳过片头片尾配置
 *
 * @param enabled 是否启用
 */
export function setSkipIntroConfig(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PLAYER_STORAGE_KEYS.SKIP_INTRO, enabled ? '1' : '0');
}

/**
 * 获取起播时间和类型
 * 优先级：历史记录 > 跳过片头 > 默认0
 * @param cid 内容ID
 * @param vid 视频ID
 * @param skipStart 当前视频的片头结束时间（可选，如果不传则从 store 读取）
 * @returns 起播时间和类型
 */
export function getStartTimeAndType(
  cid: string,
  vid: string,
  skipStart?: number
): {
  startTime: number;
  type: 'history' | 'skip_intro' | 'default';
} {
  // 1. 优先检查历史记录
  const historyRecord = useHistoryStore.getState().queryHistoryByCid(cid);
  if (historyRecord?.vid === vid && historyRecord?.videoPlayTime > 0) {
    logger.log('[player] 使用历史记录起播:', {
      cid,
      vid,
      startTime: historyRecord.videoPlayTime,
      type: 'history',
    });
    return {
      startTime: historyRecord.videoPlayTime,
      type: 'history',
    };
  }

  // 2. 检查跳过片头时间
  const skipIntroEnabled = getSkipIntroConfig();
  if (!skipIntroEnabled) return { startTime: 0, type: 'default' };

  // 优先使用传入的 skipStart，如果没有传入则从 store 读取
  const currentSkipStart = skipStart !== undefined ? skipStart : usePlayerStore.getState().skipStart;

  if (currentSkipStart > 0) {
    logger.log('[player] 使用跳过片头起播:', {
      cid,
      vid,
      startTime: currentSkipStart,
      type: 'skip_intro',
    });
    return {
      startTime: currentSkipStart,
      type: 'skip_intro',
    };
  }

  // 3. 默认从0起播
  logger.log('[player] 默认从0起播:', {
    cid,
    vid,
    startTime: 0,
    type: 'default',
  });
  return {
    startTime: 0,
    type: 'default',
  };
}
